import { SessionNotificationsArray } from '../../../interfaces/config'
import { Nullable } from '../../commons/types'

export interface ISessionVariables {
  storefrontName?: string
  sessionUid?: string
}

export enum RecommendedProductsValues {
  ML = 'ML',
  NONE = 'NONE',
}

export enum PrefillCartValues {
  LAST_ORDER = 'LAST_ORDER',
  ML = 'ML',
  NONE = 'NONE',
}

export enum WorkflowChannel {
  WHATSAPP = 'whatsapp',
  LINE = 'line',
}

interface WorkflowDictionary
  extends Record<
    string,
    {
      name?: string
      channel?: `${WorkflowChannel}`
      channelUid?: string
      stephook?: any
      jwtToken?: string
      body?: any
      ng: boolean
    }
  > {}

export interface CheckoutRulesFields {
  quantity: number
  message: string
  isActive: boolean
}

interface ProductCheckoutRules extends CheckoutRulesFields {
  sku: string[]
}

interface StockConfig {
  id: string
  warningMessage: string
  quantity: number
  enabled: boolean
}

export enum CheckoutRulesKeys {
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  cartConditionPendingUserValidation = 'cartConditionPendingUserValidation',
  cartWarningCheckReturnables = 'cartWarningCheckReturnables',
}

/**
 * Async order-confirmation parameters for a storefront.
 *
 * ⚠️ A **sibling** of `checkoutRules`, never a member of it. The checkout-rule
 * map is a closed set of keys, each with a handler; adding an unhandled key to
 * it disables that storefront's rule enforcement outright (COM-736).
 *
 * Authored per storefront on the session config. Absent for every storefront
 * that has not opted in, which is the common case and must stay the default.
 * @internal
 */
export interface IOrderConfirmationConfig {
  /**
   * Whether this storefront confirms orders asynchronously.
   *
   * The switch for the Order Detail screen's confirming/timeout states: only
   * `true` turns them on. Remote JSON, so it is compared against `true` and
   * not merely truthy — a quoted `"false"` must not opt a storefront in.
   */
  required?: boolean
  /**
   * `OrderStatus` values that count as a settled, successful order.
   *
   * Read by `deriveOrderOutcome`, which is what the in-webview confirmation
   * flow and its screen both use to decide an order has settled. Defaults to
   * `DEFAULT_SUCCESS_STATUSES` (`['CONFIRMED']`); 13 `OrderStatus` members
   * exist and tenants settle on different ones, which is why it is
   * configurable at all.
   *
   * It is **not** read by `isOrderPending`, and must not become so. That
   * predicate asks whether the backend is still working on the order; this
   * list says which statuses are final. A bare `CREATED` order is not pending
   * — it may be one nothing will ever confirm — and making it so would poll to
   * the deadline at the cost of a `completeBundlesExtra` lookup per tick.
   *
   * Members are validated against the enum by `getUnknownOrderStatuses`: a
   * typo here is a status nothing can ever match, so the order polls out and
   * reports a timeout it never had.
   *
   * Has no effect on the Order Detail screen, which shows an order that
   * already exists and derives its pending affordance from `isOrderPending`
   * alone.
   */
  successStatuses?: string[]
  /**
   * `OrderStatus` values that count as a settled, failed order.
   *
   * As `successStatuses`, and checked ahead of it, so a status listed under
   * both resolves as a failure. Defaults to `DEFAULT_FAILURE_STATUSES`
   * (`['ERROR', 'CANCELLED']`).
   */
  failureStatuses?: string[]
  /** Milliseconds between poll ticks. */
  pollingTime?: number
  /** Maximum number of poll ticks before the client gives up. */
  maxAttempts?: number
  /** Milliseconds of wall clock before the client gives up. */
  timeout?: number
}

export interface ICheckoutRules {
  [CheckoutRulesKeys.cartConditionPendingMinAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMinQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingUserValidation]: ProductCheckoutRules
  [CheckoutRulesKeys.cartWarningCheckReturnables]: ProductCheckoutRules
}

/**
 * Enum representing the types of quantity adjustment that can be applied during cart or order processing.
 *
 * - NONE: No adjustment is performed; quantities are used as provided.
 * - VALIDATE: Quantity is validated against certain criteria (such as stock or business rules), but not changed.
 * - ROUND: Quantity may be rounded according to pallets, layers, jumpQty, or other constraints.
 */
export enum QuantityAdjustment {
  /**
   * No adjustments applied to the quantity.
   */
  NONE = 'NONE',

  /**
   * Validate the quantity, but do not change it.
   */
  VALIDATE = 'VALIDATE',

  /**
   * Round the quantity as per applicable rules or constraints.
   */
  ROUND = 'ROUND',
}

interface ISessionCart {
  quantityAdjustment: `${QuantityAdjustment}`
}

interface SessionConfiguration {
  stock?: {
    hideOutOfStock?: boolean
    enabled?: boolean
    stockThreshold?: number
    key?: string
    threshold: {
      criticalLowWarning: StockConfig
      lowWarning: StockConfig
    }
  }
  i18n?: {
    language?: string
    currencyFormat?: string
    currencySymbol?: string
    currencyCode?: string
    country?: string
    /**
     * IANA time-zone name the storefront's own dates are expressed in.
     *
     * **Nothing reads this yet.** It is selected so `getOrderDetailPanelProps`
     * can forward it as the panel's `timeZone`, making an order's *timestamp*
     * render in the tenant's zone rather than the viewer's device's. That
     * forwarding lands with the `1.20.0` bump, because the panel does not
     * declare the prop before then (COM-816).
     *
     * It is **not** what fixes a delivery date. That field names a calendar day
     * and the library renders it as stored, in UTC, via
     * `formatOrderCalendarDay` — which takes no zone at all, precisely so this
     * cannot be got wrong. The defect it guards against is reaching for
     * `formatOrderDate` with a tenant zone for a delivery date, which moves it
     * to the previous day west of UTC (COM-816).
     *
     * Not to be confused with the `dateRules.timezone` the delivery-date engine
     * reads. That one lives on the storefront's **checkout-rule documents** (the
     * `CheckoutRules` model, read at
     * `storefront/packages/graphql-schemas/src/user/lib/checkoutRules/lib/dateRule.ts`),
     * *not* on `configuration.checkoutRules` — which is a sibling field on this
     * interface and has no `dateRules` member at all. Two different entities,
     * two zones, and nothing constrains them to agree.
     *
     * Typed `| null` because the field is nullable server-side and is now
     * *selected*: graphql returns an authored-but-empty field as `null`, not as
     * an absent key, so `undefined` alone could not hold the value the server
     * actually sends. The `?` is not redundant with it — `SessionDataArgs` is
     * also built as an object literal in half a dozen test files, which would
     * all have to name this field without it.
     *
     * Flatten the `null` before it reaches the panel: the prop is
     * `timeZone?: string`, and `Intl` throws on a `null` zone where it ignores
     * an absent one. `optional()` in `utils/orderDetailPanel.ts` is the helper,
     * though note it is currently used only by the order mappers in that file,
     * not yet by the props adapter.
     *
     * The five siblings above are equally nullable on the schema and equally
     * selected, and are typed `?: string` — they are under-typed, not this one.
     * Left alone deliberately: widening them is unrelated to COM-816 and would
     * put six wire-shape changes in a PR about one.
     */
    timezone?: string | null
  }
  workflow?: WorkflowDictionary
  /**
   * Server-side order splitting, read only to refuse a path it forbids.
   *
   * Cinnamon does not split anything and has no business knowing how orders
   * are chunked. It is selected because `createOrder` and `confirmOrder` both
   * `throw ERROR.BAD_REQUEST` outright when `isOrderSplit(session)` is true —
   * `'please call to the mutation "createOrders" to create a split order'` —
   * and `useCreateOrder`'s in-webview branch calls exactly those two
   * mutations. Without this field that branch would 400 on every attempt for
   * such a storefront and report a generic creation failure, with the real
   * cause (`inWebviewOrderConfirmation` authored on a splitting storefront)
   * appearing nowhere.
   *
   * The predicate mirrors `isOrderSplit`: active **and** a non-zero
   * `maxProduct`, because a splitter authored with `maxProduct: 0` splits
   * nothing and the server treats it as off.
   */
  splitters?: {
    order?: {
      active?: boolean
      maxProduct?: number
    }
  }
  checkoutRules?: ICheckoutRules
  /**
   * Async order-confirmation parameters. A sibling of `checkoutRules` — see
   * `IOrderConfirmationConfig`.
   */
  orderConfirmation?: IOrderConfirmationConfig
  groupers?: {
    cart: {
      active: boolean
      session: string[]
      product: string[]
    }
    order: {
      active: boolean
      session: string[]
      product: string[]
    }
  }
  showRecommendations?: boolean
  filters?: {
    attributes: string[]
    promotions: {
      active?: boolean
    }
    categories: {
      active?: boolean
    }
    brands: {
      active?: boolean
    }
  }
  promotions?: {
    autoAdd: boolean
    disableFreeGoodsAsDiscount: boolean | null
  }
  recommendedProducts?: {
    prefillCart: PrefillCartValues
    frequentlyBoughtTogether: RecommendedProductsValues
    suggestedItems: RecommendedProductsValues
  }
  notifications?: SessionNotificationsArray
  cart: Nullable<ISessionCart>
}

export interface Banner {
  id: string
  imagesURL: string[]
  customerType: string
  reference: string
  type: string
  isActive: boolean
  priority: number
  startDate: Date
  expirationDate: Date
  userAgent: string | null
}

export interface Customer {
  email?: string
  phoneNumber?: string[]
  code?: string
  type?: string
  customFields?: Record<string, unknown>
}

export type extraId = {
  key: string
  value: string
}

export interface SessionDataArgs {
  id?: string
  customerUid?: string
  workflow?: {
    name?: string
    channel?: `${WorkflowChannel}`
    channelUid?: string
    userUid?: string
    workflowName?: string
  }
  configuration?: SessionConfiguration
  banners?: Banner[]
  customer?: Customer
  extraIds?: extraId[]
  ownerInfo?: {
    slug: string | null
  }
}

export interface ISession {
  session: SessionDataArgs
}
